import { DateFormat } from "./Format";
/**
 * DateFormatString is mapped with the DateFormat enum above in Locale.ts.
 * Together, they determine what format is returned based on the requested DateFormat.
 * Note, LOCALE_BASED is undefined since it's format changes based on locale.
 * Date format tokens are defined here https://moment.github.io/luxon/#/parsing?id=table-of-tokens
 */
export var DateFormatString;
(function (DateFormatString) {
    DateFormatString["MMMM_D_YYYY"] = "MMMM d, yyyy";
    DateFormatString["MMM_D_YYYY"] = "MMM d, yyyy";
    DateFormatString["D_MMM_YYYY"] = "d-MMM-yyyy";
    DateFormatString["YYYY_MM_DD_HYPHEN"] = "yyyy-MM-dd";
    DateFormatString["YYYY_MM_DD_DOT"] = "yyyy.MM.dd";
    DateFormatString["DWWWW_MMMM_D_YYYY"] = "cccc, MMMM d, yyyy";
    DateFormatString["DWWW_DD_MMM_YYYY"] = "ccc dd-MMM-yyyy";
    DateFormatString["DWWW_MM_DD_YYYY"] = "ccc MM/dd/yyyy";
    DateFormatString["MMMM_D"] = "MMMM d";
    DateFormatString["D_MMMM"] = "d MMMM"; // 8 October
})(DateFormatString || (DateFormatString = {}));
/**
 * dateFormatMap is used to map the enums DateFormat (defined by cells/existing app-core)
 * to DateFormatString (the date string format based on Luxon's syntax)
 */
export var DateFormatMap = new Map([
    [DateFormat.DEFAULT_LOCALE_BASED, undefined],
    [DateFormat.MMMM_D_YYYY, DateFormatString.MMMM_D_YYYY],
    [DateFormat.MMM_D_YYYY, DateFormatString.MMM_D_YYYY],
    [DateFormat.D_MMM_YYYY, DateFormatString.D_MMM_YYYY],
    [DateFormat.YYYY_MM_DD_HYPHEN, DateFormatString.YYYY_MM_DD_HYPHEN],
    [DateFormat.YYYY_MM_DD_DOT, DateFormatString.YYYY_MM_DD_DOT],
    [DateFormat.DWWWW_MMMM_D_YYYY, DateFormatString.DWWWW_MMMM_D_YYYY],
    [DateFormat.DWWW_DD_MMM_YYYY, DateFormatString.DWWW_DD_MMM_YYYY],
    [DateFormat.DWWW_MM_DD_YYYY, DateFormatString.DWWW_MM_DD_YYYY],
    [DateFormat.MMMM_D, DateFormatString.MMMM_D],
    [DateFormat.D_MMMM, DateFormatString.D_MMMM]
]);
export var DefaultDateFormatMap = new Map([
    ["ar-AE", "dd/MM/yy"],
    ["ar-BH", "dd/MM/yy"],
    ["ar-DZ", "dd/MM/yy"],
    ["ar-EG", "dd/MM/yy"],
    ["ar-IQ", "dd/MM/yy"],
    ["ar-JO", "dd/MM/yy"],
    ["ar-KW", "dd/MM/yy"],
    ["ar-LB", "dd/MM/yy"],
    ["ar-LY", "dd/MM/yy"],
    ["ar-MA", "dd/MM/yy"],
    ["ar-OM", "dd/MM/yy"],
    ["ar-QA", "dd/MM/yy"],
    ["ar-SA", "dd/MM/yy"],
    ["ar-SD", "dd/MM/yy"],
    ["ar-SY", "dd/MM/yy"],
    ["ar-TN", "dd/MM/yy"],
    ["ar-YE", "dd/MM/yy"],
    ["be-BY", "dd.MM.yy"],
    ["bg-BG", "dd.MM.yy"],
    ["ca-ES", "dd/MM/yy"],
    ["cs-CZ", "dd.MM.yy"],
    ["da-DK", "dd-MM-yy"],
    ["de-AT", "dd.MM.yy"],
    ["de-CH", "dd.MM.yy"],
    ["de-DE", "dd.MM.yy"],
    ["de-LU", "dd.MM.yy"],
    ["el-CY", "dd/MM/yyyy"],
    ["el-GR", "dd/MM/yyyy"],
    ["en-AU", "dd/MM/yy"],
    ["en-CA", "dd/MM/yy"],
    ["en-GB", "dd/MM/yy"],
    ["en-IE", "dd/MM/yy"],
    ["en-IN", "dd/MM/yy"],
    ["en-MT", "dd/MM/yyyy"],
    ["en-NZ", "dd/MM/yy"],
    ["en-PH", "MM/dd/yy"],
    ["en-SG", "dd/MM/yy"],
    ["en-US", "MM/dd/yy"],
    ["en-ZA", "yyyy/MM/dd"],
    ["es-AR", "dd/MM/yy"],
    ["es-BO", "dd-MM-yy"],
    ["es-CL", "dd-MM-yy"],
    ["es-CO", "dd/MM/yy"],
    ["es-CR", "dd/MM/yy"],
    ["es-DO", "dd/MM/yy"],
    ["es-EC", "dd/MM/yy"],
    ["es-ES", "dd/MM/yy"],
    ["es-GT", "dd/MM/yy"],
    ["es-HN", "MM-dd-yy"],
    ["es-MX", "dd/MM/yy"],
    ["es-NI", "MM-dd-yy"],
    ["es-PA", "MM/dd/yy"],
    ["es-PE", "dd/MM/yy"],
    ["es-PR", "MM-dd-yy"],
    ["es-PY", "dd/MM/yy"],
    ["es-SV", "MM-dd-yy"],
    ["es-US", "MM/dd/yy"],
    ["es-UY", "dd/MM/yy"],
    ["es-VE", "dd/MM/yy"],
    ["et-EE", "dd.MM.yy"],
    ["fi-FI", "dd.MM.yyyy"],
    ["fr-BE", "dd/MM/yy"],
    ["fr-CA", "yy-MM-dd"],
    ["fr-CH", "dd.MM.yy"],
    ["fr-FR", "dd/MM/yy"],
    ["fr-LU", "dd/MM/yy"],
    ["ga-IE", "dd/MM/yyyy"],
    ["hi-US", "MM/dd/yy"],
    ["hr-HR", "dd.MM.yy."],
    ["hu-HU", "yyyy.MM.dd."],
    ["in-ID", "dd/MM/yy"],
    ["is-IS", "dd.MM.yyyy"],
    ["it-CH", "dd.MM.yy"],
    ["it-IT", "dd/MM/yy"],
    ["iw-IL", "dd/MM/yy"],
    ["ja-JP", "yy/MM/dd"],
    ["ko-KR", "yy. MM. dd"],
    ["lt-LT", "yy.MM.dd"],
    ["lv-LV", "yy.dd.MM"],
    ["mk-MK", "dd.MM.yy"],
    ["ms-MY", "dd/MM/yyyy"],
    ["mt-MT", "dd/MM/yyyy"],
    ["nl-BE", "dd/MM/yy"],
    ["nl-NL", "dd-MM-yy"],
    ["no-NO", "dd.MM.yy"],
    ["pl-PL", "dd.MM.yy"],
    ["pt-BR", "dd/MM/yy"],
    ["pt-PT", "dd-MM-yyyy"],
    ["ro-RO", "dd.MM.yyyy"],
    ["ru-RU", "dd.MM.yy"],
    ["sk-SK", "dd.MM.yyyy"],
    ["sl-SI", "dd.MM.yyyy"],
    ["sq-AL", "yy-MM-dd"],
    ["sr-BA", "yy-MM-dd"],
    ["sr-CS", "dd.MM.yy."],
    ["sv-SE", "yyyy-MM-dd"],
    ["th-US", "dd/MM/yyyy"],
    ["tr-TR", "dd.MM.yyyy"],
    ["uk-UA", "dd.MM.yy"],
    ["vi-VN", "dd/MM/yyyy"],
    ["zh-CN", "yy-MM-dd"],
    ["zh-HK", "yy年MM月dd日"],
    ["zh-SG", "dd/MM/yy"],
    ["zh-TW", "yyyy/MM/dd"] // Chinese - Taiwan
]);
export var DefaultDateTimeFormatMap = new Map([
    ["ar-AE", "dd/MM/yy hh:mm a"],
    ["ar-BH", "dd/MM/yy hh:mm a"],
    ["ar-DZ", "dd/MM/yy hh:mm a"],
    ["ar-EG", "dd/MM/yy hh:mm a"],
    ["ar-IQ", "dd/MM/yy hh:mm a"],
    ["ar-JO", "dd/MM/yy hh:mm a"],
    ["ar-KW", "dd/MM/yy hh:mm a"],
    ["ar-LB", "dd/MM/yy hh:mm a"],
    ["ar-LY", "dd/MM/yy hh:mm a"],
    ["ar-MA", "dd/MM/yy hh:mm a"],
    ["ar-OM", "dd/MM/yy hh:mm a"],
    ["ar-QA", "dd/MM/yy hh:mm a"],
    ["ar-SA", "dd/MM/yy hh:mm a"],
    ["ar-SD", "dd/MM/yy hh:mm a"],
    ["ar-SY", "dd/MM/yy hh:mm a"],
    ["ar-TN", "dd/MM/yy hh:mm a"],
    ["ar-YE", "dd/MM/yy hh:mm a"],
    ["be-BY", "dd.MM.yy H:mm"],
    ["bg-BG", "dd.MM.yy H:mm"],
    ["ca-ES", "dd/MM/yy HH:mm"],
    ["cs-CZ", "dd.MM.yy H:mm"],
    ["da-DK", "dd-MM-yy HH:mm"],
    ["de-AT", "dd.MM.yy HH:mm"],
    ["de-CH", "dd.MM.yy HH:mm"],
    ["de-DE", "dd.MM.yy HH:mm"],
    ["de-LU", "dd.MM.yy HH:mm"],
    ["el-CY", "dd/MM/yyyy h:mm a"],
    ["el-GR", "dd/MM/yyyy h:mm a"],
    ["en-AU", "dd/MM/yy h:mm a"],
    ["en-CA", "dd/MM/yy h:mm a"],
    ["en-GB", "dd/MM/yy HH:mm"],
    ["en-IE", "dd/MM/yy h:mm a"],
    ["en-IN", "dd/MM/yy h:mm a"],
    ["en-MT", "dd/MM/yyyy HH:mm"],
    ["en-NZ", "dd/MM/yy h:mm a"],
    ["en-PH", "MM/dd/yy h:mm a"],
    ["en-SG", "dd/MM/yy h:mm a"],
    ["en-US", "MM/dd/yy h:mm a"],
    ["en-ZA", "yyyy/MM/dd h:mm a"],
    ["es-AR", "dd/MM/yy HH:mm"],
    ["es-BO", "dd-MM-yy hh:mm a"],
    ["es-CL", "dd-MM-yy H:mm"],
    ["es-CO", "dd/MM/yy hh:mm a"],
    ["es-CR", "dd/MM/yy hh:mm a"],
    ["es-DO", "dd/MM/yy hh:mm a"],
    ["es-EC", "dd/MM/yy H:mm"],
    ["es-ES", "dd/MM/yy H:mm"],
    ["es-GT", "dd/MM/yy hh:mm a"],
    ["es-HN", "MM-dd-yy hh:mm a"],
    ["es-MX", "dd/MM/yy hh:mm a"],
    ["es-NI", "MM-dd-yy hh:mm a"],
    ["es-PA", "MM/dd/yy hh:mm a"],
    ["es-PE", "dd/MM/yy hh:mm a"],
    ["es-PR", "MM-dd-yy hh:mm a"],
    ["es-PY", "dd/MM/yy hh:mm a"],
    ["es-SV", "MM-dd-yy hh:mm a"],
    ["es-US", "MM/dd/yy h:mm a"],
    ["es-UY", "dd/MM/yy hh:mm a"],
    ["es-VE", "dd/MM/yy hh:mm a"],
    ["et-EE", "dd.MM.yy H:mm"],
    ["fi-FI", "dd.MM.yyyy H:mm"],
    ["fr-BE", "dd/MM/yy H:mm"],
    ["fr-CA", "yy-MM-dd HH:mm"],
    ["fr-CH", "dd.MM.yy HH:mm"],
    ["fr-FR", "dd/MM/yy HH:mm"],
    ["fr-LU", "dd/MM/yy HH:mm"],
    ["ga-IE", "dd/MM/yyyy HH:mm"],
    ["hi-US", "MM/dd/yy h:mm a"],
    ["hr-HR", "dd.MM.yy. HH:mm"],
    ["hu-HU", "yyyy.MM.dd. H:mm"],
    ["in-ID", "dd/MM/yy H:mm"],
    ["is-IS", "dd.MM.yyyy HH:mm"],
    ["it-CH", "dd.MM.yy HH:mm"],
    ["it-IT", "dd/MM/yy H:mm"],
    ["iw-IL", "dd/MM/yy HH:mm"],
    ["ja-JP", "yy/MM/dd H:mm"],
    ["ko-KR", "yy. MM. dd h:mm a"],
    ["lt-LT", "yy.MM.dd HH:mm"],
    ["lv-LV", "yy.dd.MM HH:mm"],
    ["mk-MK", "dd.MM.yy HH:mm"],
    ["ms-MY", "dd/MM/yyyy H:mm"],
    ["mt-MT", "dd/MM/yyyy HH:mm"],
    ["nl-BE", "dd/MM/yy H:mm"],
    ["nl-NL", "dd-MM-yy H:mm"],
    ["no-NO", "dd.MM.yy HH:mm"],
    ["pl-PL", "dd.MM.yy HH:mm"],
    ["pt-BR", "dd/MM/yy HH:mm"],
    ["pt-PT", "dd-MM-yyyy H:mm"],
    ["ro-RO", "dd.MM.yyyy HH:mm"],
    ["ru-RU", "dd.MM.yy H:mm"],
    ["sk-SK", "dd.MM.yyyy H:mm"],
    ["sl-SI", "dd.MM.yyyy H:mm"],
    ["sq-AL", "yy-MM-dd h:mm a"],
    ["sr-BA", "yy-MM-dd HH:mm"],
    ["sr-CS", "dd.MM.yy. HH:mm"],
    ["sv-SE", "yyyy-MM-dd HH:mm"],
    ["th-US", "dd/MM/yyyy HH:mm"],
    ["tr-TR", "dd.MM.yyyy HH:mm"],
    ["uk-UA", "dd.MM.yy H:mm"],
    ["vi-VN", "dd/MM/yyyy HH:mm"],
    ["zh-CN", "yy-MM-dd h:mm a"],
    ["zh-HK", "yy年MM月dd日 h:mm a"],
    ["zh-SG", "dd/MM/yy hh:mm a"],
    ["zh-TW", "yyyy/MM/dd h:mm a"] // Chinese - Taiwan
]);
//# sourceMappingURL=Locale.js.map